import React, { useEffect, useRef, useState } from "react";
import { useHistory } from "react-router-dom";
import { Button, Card, Tabs, Typography, Space, Tooltip, Tag, Divider, Row, Col } from "antd";
import { t } from "i18next";
import {
    EditOutlined,
    DeleteOutlined,
    UndoOutlined,
    WalletOutlined,
    FileTextOutlined,
    PrinterOutlined,
    MoreOutlined,
    BarcodeOutlined,
    ApiOutlined,
    SendOutlined,
    CopyOutlined,
    CheckOutlined
} from '@ant-design/icons';
import { MdOutlineMedicalServices } from "react-icons/md";
import moment from "moment";
import Helper from "../../../core/Helper";

// Context & Services
import { PERMISSIONS, useUser } from "../../../core/context/UserContext";
import ApiService from "../../../core/service/ApiService";
import { useModal } from "../../../core/context/ModalContext";

// Components
import AppointmentPreview from "./AppointmentPreview";
import OperationsPreview from "./OperationsPreview";
import PriceSummaryPreview from "./PriceSummaryPreview";
import PaymentsPreview from "./PaymentsPreview";
import LogsPreview from "./LogsPreview";
import StateChanger from "../StateChanger";
import FormDownload from "../FormDownload";
import BarkodePrinter from "../BarcodePrinter";
import AppointmentFiles from "../files/AppointmentFiles";
import Hl7MessagesPreview from "./Hl7MessagesPreview";
import { Alert, Select, Switch, message } from "antd";
import * as qz from "qz-tray";
import { isMobile } from "react-device-detect";

const { Title, Text } = Typography;
const { TabPane } = Tabs;

export default function AppointmentPreviewApp({ appointmentId, onDelete }) {
    const history = useHistory();
    const { hasPermission } = useUser();
    const [activeTab, setActiveTab] = useState('appointment');
    const [barcodePdf, setBarcodePdf] = useState('');
    const [sendEnabizLoading, setSendEnabizLoading] = useState(false);
    const [copiedAll, setCopiedAll] = useState(false);
    const { user } = useUser();
    const wrapperRef = useRef(null);

    // Bu component'i içeren modal-body'nin padding'ini kaldır (sadece bu modal'ı etkiler)
    useEffect(() => {
        const modalBody = wrapperRef.current?.closest('.ant-modal-body');
        if (!modalBody) return;
        const originalPadding = modalBody.style.padding;
        modalBody.style.padding = '0';
        return () => {
            modalBody.style.padding = originalPadding;
        };
    }, []);

    // State
    const [appointment, setAppointment] = useState({
        id: null,
        patientId: '',
        patient: {},
        unit: {},
        doctor: {},
        institution: {},
        appointmentOperations: [],
        appointmentOtherOperations: [],
        state: {} // state.id, state.color, state.name
    });
    const [payments, setPayments] = useState([]);
    const [logs, setLogs] = useState([]);

    // --- API CALLS ---
    const getAppointment = async () => {
        if (appointmentId) {
            const res = await ApiService.Appointment.GetAppointment(appointmentId);
            if (res.success) setAppointment(res.data);
        }
    }

    const getPayments = async () => {
        if (appointmentId) {
            const res = await ApiService.Payment.GetPaymentList(appointmentId);
            if (res.success) setPayments(res.data);
        }
    }

    // Log sekmesi APPLOG_VIEW yetkisine bağlı; yetkisi olmayan kullanıcı için istek de atılmamalı.
    const getLogs = async () => {
        if (appointmentId && hasPermission(PERMISSIONS.APPLOG_VIEW)) {
            const res = await ApiService.AppLog.GetAppLogsByAppointmentId(appointmentId);
            if (res.success) setLogs(res.data);
        }
    }

    const onAppointmentId = async () => {
        await getAppointment();
        await getPayments();
        await getLogs();
    }

    useEffect(() => {
        onAppointmentId();
    }, [appointmentId]);

    // E-Nabız otomatik gönderimi yalnızca Kurum + Birim + Randevu Tipi üçü de aktifse çalışır.
    // (AppointmentDetailEdit'teki enabizConfigEnabled ile aynı kural.) Üçünden biri pasifse
    // elle gönderim yine mümkün ama kullanıcı uyarılıyor.
    const enabizConfigEnabled =
        appointment?.unit?.enabizEnabled === true &&
        appointment?.institution?.enabizEnabled === true &&
        appointment?.appointmentType?.enabizEnabled === true;

    // --- ACTIONS ---
    const goPayments = () => {
        // Tercihen history.push kullanın ama window.location da çalışır
        window.location.href = `/app/patients/${appointment.patientId}/appointments/${appointmentId}/payments`;
    }

    const openPatientResults = async () => {
        const res = await ApiService.Patient.GetToken(appointment.patientId);
        if (res.success) {
            const url = `${window.AppSettings.server.result}?access_token=${res.data}`;
            window.open(url, '_blank');
        }
    }

    const deleteAppointment = async () => {
        const res = await ApiService.Appointment.DeleteAppointment(appointmentId);
        if (res.success) onDelete && onDelete();
    }

    const restoreAppointment = async () => {
        const res = await ApiService.Appointment.RestoreAppointment(appointmentId);
        if (res.success) onDelete && onDelete();
    }

    const editAppointment = () => {
        window.location.href = `/app/patients/${appointment.patientId}/appointments/${appointmentId}`;
    }

    const onStateChange = async (stateId, stateDescForm) => {
        const res = await ApiService.Appointment.EditAppointmentState(appointmentId, {
            stateId,
            stateDescForm
        });
        if (res.success) await getAppointment();
    }

    const [checkInLoading, setCheckInLoading] = useState(false);
    const toggleCheckIn = async (checked) => {
        setCheckInLoading(true);
        try {
            const res = await ApiService.Appointment.CheckIn(appointmentId, { checkIn: checked });
            if (res.success) {
                await getAppointment();
            }
        } finally {
            setCheckInLoading(false);
        }
    }

    const printBarcode = async (e) => {
        e.stopPropagation();
        const res = await ApiService.Barcode.GetBarcode(appointmentId);
        if (res.success) {
            setBarcodePdf(res.data);
        } else {
            message.error(t('Could not get the PDF'));
        }
    };

    const buildAppointmentText = () => {
        const lines = [];
        const sep = '═══════════════════════════════════════';
        const subSep = '───────────────────────────────────────';
        const pad = (label) => label.padEnd(13, ' ');

        lines.push(sep);
        lines.push(t('APPOINTMENT INFORMATION'));
        lines.push(sep);
        lines.push('');

        // HASTA BİLGİLERİ
        const p = appointment.patient || {};
        const patientLines = [];
        const fullName = `${p.name || ''} ${p.lastname || ''}`.trim();
        if (fullName) patientLines.push(`${pad('Ad Soyad')}: ${fullName}`);
        if (p.customId) {
            const idStr = p.otherPatientId ? `${p.customId} (Diğer ID: ${p.otherPatientId})` : p.customId;
            patientLines.push(`${pad('Hasta ID')}: ${idStr}`);
        }
        if (p.gender) patientLines.push(`${pad('Cinsiyet')}: ${t(p.gender)}`);
        if (p.birthDay && p.birthMonth && p.birthYear) {
            const bd = `${String(p.birthDay).padStart(2, '0')}/${String(p.birthMonth).padStart(2, '0')}/${p.birthYear}`;
            patientLines.push(`${pad(t('Birthdate'))}: ${bd}`);
        }
        if (p.motherName || p.fatherName) {
            patientLines.push(`${pad('Anne / Baba')}: ${p.motherName || '-'} / ${p.fatherName || '-'}`);
        }
        if (p.phone1) patientLines.push(`${pad('Telefon')}: ${Helper.Phone.intlPhoneFormat(p.phoneCode1, p.phone1)}`);
        if (p.phone2) patientLines.push(`${pad('Telefon 2')}: ${Helper.Phone.intlPhoneFormat(p.phoneCode2, p.phone2)}`);
        if (p.email) patientLines.push(`${pad('E-posta')}: ${p.email}`);
        if (p.note) patientLines.push(`${pad('Hasta Notu')}: ${p.note}`);

        if (patientLines.length) {
            lines.push('▸ ' + t('PATIENT INFORMATION'));
            lines.push(subSep);
            lines.push(...patientLines);
            lines.push('');
        }

        // RANDEVU DETAYLARI
        const detailLines = [];
        if (appointment.institution?.name) detailLines.push(`${pad('Kurum')}: ${appointment.institution.name}`);
        if (appointment.unit?.name) detailLines.push(`${pad('Birim')}: ${appointment.unit.name}`);
        if (appointment.doctor?.name) detailLines.push(`${pad('Doktor')}: ${appointment.doctor.name}`);
        if (appointment.appointmentType?.name) detailLines.push(`${pad('Randevu Tipi')}: ${appointment.appointmentType.name}`);
        if (appointment.startDate) {
            const date = moment(appointment.startDate).format('DD/MM/YYYY');
            const day = moment(appointment.startDate).format('dddd');
            detailLines.push(`${pad('Tarih')}: ${date} (${t(day)})`);
            const startTime = moment(appointment.startDate).format('HH:mm');
            const endTime = appointment.endDate ? moment(appointment.endDate).format('HH:mm') : '';
            detailLines.push(`${pad('Saat')}: ${startTime}${endTime ? ' - ' + endTime : ''}`);
        }
        if (appointment.state?.name) detailLines.push(`${pad('Durum')}: ${appointment.state.name}`);
        if (appointment.checkIn && appointment.checkInDate) {
            detailLines.push(`${pad('Check-in')}: ${moment(appointment.checkInDate).format('HH:mm')}`);
        }
        if (appointment.anamnez) detailLines.push(`${pad('Anamnez')}: ${appointment.anamnez}`);
        if (appointment.note) detailLines.push(`${pad('Not')}: ${appointment.note}`);

        if (detailLines.length) {
            lines.push('▸ RANDEVU DETAYLARI');
            lines.push(subSep);
            lines.push(...detailLines);
            lines.push('');
        }

        // İŞLEMLER
        const ops = appointment.appointmentOperations || [];
        const otherOps = appointment.appointmentOtherOperations || [];
        if (ops.length || otherOps.length) {
            lines.push('▸ ' + t('OPERATIONS'));
            lines.push(subSep);
            let idx = 1;
            ops.forEach((op) => {
                const name = op.operation?.name || '-';
                const acc = op.accessionNumber ? ` [ACC: ${op.accessionNumber}]` : '';
                lines.push(`${idx}. ${name}${acc}`);
                idx++;
            });
            otherOps.forEach((op) => {
                lines.push(`${idx}. ${op.name || '-'} (diğer)`);
                idx++;
            });
            lines.push('');
        }

        // İŞLEM BİLGİLERİ
        lines.push('▸ ' + t('OPERATION INFORMATION'));
        lines.push(subSep);
        lines.push(`${pad('Randevu ID')}: ${appointment.id || appointmentId}`);
        lines.push(sep);

        return lines.join('\n');
    };

    const copyAllInfo = async () => {
        try {
            const text = buildAppointmentText();
            await navigator.clipboard.writeText(text);
            setCopiedAll(true);
            setTimeout(() => setCopiedAll(false), 2000);
        } catch (err) {
            message.error(t('Copy failed') + ' ❌');
        }
    };

    const sendBarcodeToSelectedPrinter = async () => {
        try {
            if (!qz.websocket.isActive()) {
                await qz.websocket.connect({ verify: false });
            }

            const res = await ApiService.Barcode.GetBarcode(appointmentId);
            if (!res.success || !res.data) {
                message.error(t('Could not get the PDF'));
                return;
            }

            const pdfUrl = res.data;
            const pdfResponse = await fetch(pdfUrl);
            const pdfBlob = await pdfResponse.blob();
            const pdfArrayBuffer = await pdfBlob.arrayBuffer();
            const pdfBase64 = btoa(String.fromCharCode(...new Uint8Array(pdfArrayBuffer)));
            if (!user.printerName) {
                message.warning(t('You must select a printer on the user page!'));
                return;
            }
            const config = qz.configs.create(user.printerName);
            const data = [{ type: 'pdf', format: 'base64', data: pdfBase64 }];

            await qz.print(config, data);

        } catch (err) {
            message.error(t('Printing failed'));
            console.log(err);
        }
    };

    return (
        <div ref={wrapperRef} style={{ minHeight: '100vh', backgroundColor: '#f5f7fa', paddingBottom: 40 }}>

            {/* --- HEADER (ACTION BAR) --- */}
            <div style={{
                backgroundColor: '#fff',
                padding: '12px 12px',
                borderBottom: '1px solid #e8e8e8',
                boxShadow: '0 2px 4px rgba(0,0,0,0.02)',
                marginBottom: 24,
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                flexWrap: 'wrap',
                gap: 16
            }}>
                {/* SOL: Başlık, ID ve Durum */}
                <div style={{ minWidth: 200 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                        <Title level={4} style={{ margin: 0 }}>{t('Appointment Detail')}</Title>
                        {appointment.state?.name && (
                            <Tag color={appointment.state.color} style={{ margin: 0 }}>
                                {appointment.state.name}
                            </Tag>
                        )}
                    </div>
                    <div style={{ marginTop: 4 }}>
                        <Text type="secondary" style={{ fontSize: 13 }}>ID: </Text>
                        <Text strong copyable style={{ fontSize: 13 }}>{appointmentId}</Text>
                    </div>
                </div>

                {/* SAĞ: Aksiyon Butonları */}
                <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap', justifyContent: isMobile ? 'flex-start' : 'flex-end' }}>

                    {/* 1. Durum Değiştirici */}
                    <StateChanger
                        onStateChange={onStateChange}
                        appointmentId={appointmentId}
                        selectedStateId={appointment.state.id}
                        checkIn={appointment.checkIn}
                    />

                    {/* 1b. Check-in Switch */}
                    <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 6,
                        padding: '0 8px',
                        background: appointment.checkIn ? '#f6ffed' : '#fafafa',
                        border: `1px solid ${appointment.checkIn ? '#b7eb8f' : '#d9d9d9'}`,
                        borderRadius: 4,
                        height: 28
                    }}>
                        <Text style={{ fontSize: 13, whiteSpace: 'nowrap' }}>
                            {t('Check-in')}
                        </Text>
                        <Switch
                            size="small"
                            loading={checkInLoading}
                            checked={!!appointment.checkIn}
                            onChange={toggleCheckIn}
                        />
                    </div>

                    {/* 2. Ana İşlemler (Renkli Butonlar) */}
                    <Space size={'small'}>
                        {hasPermission(PERMISSIONS.APPOINTMENT_CREATE_EDIT) && (
                            <Button size={'small'} type="primary" icon={<EditOutlined />} onClick={editAppointment}>
                                {t('Edit')}
                            </Button>
                        )}

                        {hasPermission(PERMISSIONS.PAYMENT_CREATE_EDIT) && (
                            <Button
                                size={'small'}
                                type="primary"
                                icon={<WalletOutlined />}
                                onClick={goPayments}
                                style={{ backgroundColor: '#52c41a', borderColor: '#52c41a' }}
                            >
                                {t('Pay')}
                            </Button>
                        )}
                        <Tooltip title={t('Patient Results')}>
                            <Button size={'small'} icon={<FileTextOutlined />} onClick={openPatientResults} />
                        </Tooltip>

                        {/* Form İndirme Bileşeni */}
                        <FormDownload appointmentId={appointmentId} unitId={appointment?.unit?.id} />

                    </Space>

                    {/* 3. Yardımcı İşlemler (Standart Butonlar) */}
                    <Space wrap>
                        {/* Tümünü Kopyala */}
                        <Tooltip title={copiedAll ? t('Copied') : t('Copy All Information')}>
                            <Button
                                size={'small'}
                                icon={copiedAll ? <CheckOutlined style={{ color: '#52c41a' }} /> : <CopyOutlined />}
                                onClick={copyAllInfo}
                            >
                                {copiedAll ? t('Copied') : t('Copy All')}
                            </Button>
                        </Tooltip>

                        {/* Barkod İşlemleri */}
                        {hasPermission(PERMISSIONS.APPOINTMENT_PRINTER_BARCODE) && (
                            <Tooltip title={t('Print Barcode')}>
                                <Button size={'small'} icon={<PrinterOutlined />} onClick={printBarcode}>
                                    {t('Print Barcode')}
                                </Button>
                            </Tooltip>
                        )}

                        {hasPermission(PERMISSIONS.APPOINTMENT_SEND_BARCODE) && (
                            <Tooltip title={t('Send Barcode')}>
                                <Button size={'small'} icon={<BarcodeOutlined />} onClick={sendBarcodeToSelectedPrinter}>
                                    {t('Send Barcode')}
                                </Button>
                            </Tooltip>
                        )}

                        {/* Silme İşlemi */}
                        {hasPermission(PERMISSIONS.APPOINTMENT_DELETE) && (
                            <Tooltip title={!appointment?.isDeleted ? t('Delete') : t('Restore')}>
                                <Button
                                    size={'small'}
                                    danger
                                    icon={!appointment?.isDeleted ? <DeleteOutlined /> : <UndoOutlined />}
                                    onClick={!appointment?.isDeleted ? deleteAppointment : restoreAppointment}
                                />
                            </Tooltip>
                        )}
                    </Space>

                    {/* Gizli Yazdırma Frame'i */}
                    <iframe
                        id="print-pdf-barkode"
                        src={barcodePdf}
                        style={{ display: 'none', width: 0, height: 0, position: 'absolute' }}
                        title="barcode-printer"
                        onLoad={(e) => {
                            if (barcodePdf) {
                                const frameWindow = e.target.contentWindow;
                                if (frameWindow) {
                                    frameWindow.focus();
                                    frameWindow.print();
                                }
                            }
                        }}
                    />
                </div>
            </div>

            {/* --- CONTENT --- */}
            <div style={{ padding: '0 12px' }}>
                <Tabs
                    activeKey={activeTab}
                    onChange={setActiveTab}
                    type="card"
                    size="large"
                    style={{ marginBottom: 32 }}
                    className="modern-tabs"
                >
                    {/* --- TAB 1: RANDEVU & İŞLEMLER --- */}
                    <TabPane tab={<span><MdOutlineMedicalServices style={{ marginRight: 8, position: 'relative', top: 2 }} />{t('Appointment')}</span>} key="appointment">

                        <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
                            {/* 1. HASTA VE RANDEVU BİLGİLERİ (BAĞLAM) - Nötr/Gri Ton */}
                            <AppointmentPreview appointment={appointment} patient={appointment.patient} />

                            {/* 2. İŞLEMLER (KLİNİK) - Mor Vurgu (PatientApp ile aynı bölüm = aynı renk) */}
                            <Card
                                title={<span style={{ color: '#531dab' }}>{t('Operations')}</span>}
                                bordered={false}
                                style={{
                                    borderRadius: 8,
                                    borderTop: '4px solid #722ed1'
                                }}
                                headStyle={{ borderBottom: '1px solid #f9f0ff', backgroundColor: '#fafafa' }}
                                extra={
                                    appointment?.state?.stateGroup?.id === 2 && hasPermission(PERMISSIONS.ENABIZ) && (
                                        <Button
                                            type="primary"
                                            size="small"
                                            icon={<SendOutlined />}
                                            loading={sendEnabizLoading}
                                            onClick={async () => {
                                                setSendEnabizLoading(true);
                                                try {
                                                    const res = await ApiService.AppointmentTeletip.SendEnabizBatch(appointmentId);
                                                    if (res.success) {
                                                        message.success(t('E-Nabız sent successfully. Count: ') + res.data);
                                                        await getAppointment();
                                                    }
                                                } finally {
                                                    setSendEnabizLoading(false);
                                                }
                                            }}
                                        >
                                            {t('Send E-Nabız')}
                                        </Button>
                                    )
                                }
                            >
                                {appointment?.state?.stateGroup?.id === 2 && hasPermission(PERMISSIONS.ENABIZ) && !enabizConfigEnabled && (
                                    <Alert
                                        type="warning"
                                        showIcon
                                        style={{ marginBottom: 12 }}
                                        message={t('E-Nabız sending appears to be disabled for the institution / unit / appointment type. Please inform IT before sending.')}
                                    />
                                )}
                                <OperationsPreview
                                    operations={appointment.appointmentOperations.map(x => ({ ...x, appointmentId, startDate: appointment.startDate, currency: appointment.currency }))}
                                    otherOperations={appointment.appointmentOtherOperations.map(x => ({ ...x, appointmentId, startDate: appointment.startDate, currency: appointment.currency }))}
                                />
                            </Card>

                            {/* 3. FİNANSAL BİLGİLER (FİNANS) - Yeşil Vurgu */}
                            {hasPermission(PERMISSIONS.PAYMENT_VIEW) && (
                                <Card
                                    title={<span style={{ color: '#389e0d' }}>{t('Financial Information')}</span>}
                                    bordered={false}
                                    style={{
                                        borderRadius: 8,
                                        borderTop: '4px solid #52c41a'
                                    }}
                                    headStyle={{ borderBottom: '1px solid #f6ffed', backgroundColor: '#fafafa' }}
                                >
                                    <Row gutter={[24, 24]}>
                                        <Col xs={24} xl={16}>
                                            <div style={{ marginBottom: 16 }}>
                                                <Title level={5} style={{ color: '#8c8c8c', marginBottom: 12 }}>{t('Payment History')}</Title>
                                                <PaymentsPreview payments={payments} appointmentCurrency={appointment.currency} />
                                            </div>
                                        </Col>
                                        <Col xs={24} xl={8}>
                                            <div style={{ padding: 16, backgroundColor: '#f6ffed', borderRadius: 8, border: '1px solid #b7eb8f' }}>
                                                <Title level={5} style={{ marginBottom: 16, color: '#389e0d' }}>{t('Summary')}</Title>
                                                <PriceSummaryPreview
                                                    totalPrice={appointment.totalPrice}
                                                    totalPaid={appointment.totalPaid}
                                                    currency={appointment.currency}
                                                />
                                            </div>
                                        </Col>
                                    </Row>
                                </Card>
                            )}
                        </div>
                    </TabPane>

                    {/* --- TAB 3: DOSYALAR --- */}
                    {hasPermission(PERMISSIONS.APPOINTMENT_VIEW) && (
                        <TabPane tab={<span><FileTextOutlined /> {t('Files')}</span>} key="files">
                            <Card title={t('Attached Files')} bordered={false} style={{ borderRadius: 8 }}>
                                <AppointmentFiles appointmentId={appointment.id} />
                            </Card>
                        </TabPane>
                    )}

                    {/* --- TAB 4: HL7 MESAJLARI --- */}
                    {hasPermission(PERMISSIONS.APPOINTMENT_VIEW) && (
                        <TabPane tab={<span><ApiOutlined /> {t('HL7 Messages')}</span>} key="hl7messages">
                            <Card title={t('HL7 Messages')} bordered={false} style={{ borderRadius: 8 }}>
                                <Hl7MessagesPreview appointmentId={appointment.id} />
                            </Card>
                        </TabPane>
                    )}

                    {/* --- TAB 5: LOGLAR --- */}
                    {hasPermission(PERMISSIONS.APPLOG_VIEW) && (
                        <TabPane tab={<span><MoreOutlined /> {t('Logs')}</span>} key="logs">
                            <Card title={t('Activity Logs')} bordered={false} style={{ borderRadius: 8 }}>
                                <LogsPreview logs={logs} />
                            </Card>
                        </TabPane>
                    )}
                </Tabs>
            </div>
        </div>
    );
}